import jp from 'jsonpath';
import type { EntityRootData, EntityDetailSchema } from '@web-config-app/core';
import {
  findSchemaPathsWhere,
  convertSchemaPathToJsonPath,
  hasEntityImportAnnotation,
} from '@web-config-app/schema-utils';

/**
 * Creates a factory for tenant-scoped transformers by pre-computing the schema paths once.
 *
 * This is more efficient when transforming multiple entities of the same type,
 * as the schema traversal (findSchemaPathsWhere) only happens once rather than
 * for each entity.
 *
 * @param schema - The entity schema with x-entity-import annotations
 * @returns A factory function that creates transformers for specific source/target tenant pairs
 *
 * @example
 * ```typescript
 * // Create factory once before processing entities
 * const createTransformer = createTenantScopedTransformer(configEntity.schema);
 *
 * // Inside loop, create transformer for each entity's sourceTenantId
 * entities.forEach(entity => {
 *   const transformer = createTransformer(entity.sourceTenantId, targetTenantId);
 *   const transformedData = transformer(entity.data);
 * });
 * ```
 */
export const createTenantScopedTransformer = (schema: EntityDetailSchema) => {
  // Find all paths with x-entity-import: { replace: ['tenantId'] } annotation upfront
  const tenantScopedPaths = Array.from(
    findSchemaPathsWhere(schema, hasEntityImportAnnotation('tenantId')),
  ).map((schemaPath) => convertSchemaPathToJsonPath(schemaPath));

  /**
   * Creates a transformer to replace tenant-specific data from the source tenant to the target tenant.
   *
   * @param sourceTenantId - The tenant ID from which the entity was exported
   * @param targetTenantId - The tenant ID to which the entity is being imported
   * @returns A transformer function that replaces tenant-specific data in annotated fields
   */
  return (sourceTenantId: string | undefined, targetTenantId: string) => {
    // No transformation needed if source and target are the same or source is missing
    if (!sourceTenantId || sourceTenantId === targetTenantId) {
      return (data: EntityRootData): EntityRootData => data;
    }

    // No annotated fields in schema - we can just skip and return data
    if (tenantScopedPaths.length === 0) {
      return (data: EntityRootData): EntityRootData => data;
    }

    return (data: EntityRootData): EntityRootData => {
      // Deep clone to avoid mutating original
      const transformedData: EntityRootData = JSON.parse(JSON.stringify(data));

      tenantScopedPaths.forEach((jsonPath) => {
        const nodes = jp.nodes(transformedData, jsonPath);

        nodes.forEach(({ path: nodePath, value }) => {
          if (typeof value === 'string' && value.length > 0) {
            const transformedValue = value.replaceAll(
              sourceTenantId,
              targetTenantId,
            );
            if (transformedValue !== value) {
              const nodeJsonPath = jp.stringify(nodePath);
              jp.value(transformedData, nodeJsonPath, transformedValue);
            }
          }
        });
      });

      return transformedData;
    };
  };
};
